import { useState, useMemo, useCallback } from 'react';
import { PropertySlider } from './PropertySlider';
import { RevenueDisplay } from './RevenueDisplay';

const CITY_DATA: Record<
  string,
  { baseRate: number; occupancy: number; multiplier: number }
> = {
  Vancouver: { baseRate: 165, occupancy: 78, multiplier: 1.0 },
  'North Vancouver': { baseRate: 155, occupancy: 74, multiplier: 0.95 },
  Burnaby: { baseRate: 135, occupancy: 72, multiplier: 0.85 },
  Surrey: { baseRate: 120, occupancy: 68, multiplier: 0.78 },
  Langley: { baseRate: 115, occupancy: 65, multiplier: 0.75 },
  Victoria: { baseRate: 150, occupancy: 76, multiplier: 0.93 },
  Whistler: { baseRate: 220, occupancy: 72, multiplier: 1.35 },
  Calgary: { baseRate: 130, occupancy: 70, multiplier: 0.82 },
  Edmonton: { baseRate: 115, occupancy: 66, multiplier: 0.73 },
  Toronto: { baseRate: 170, occupancy: 80, multiplier: 1.05 },
};

// Maximum nights allowed per year by city and property type
const NIGHT_CAPS: Record<string, { entireSpace: number | null; privateRoom: number | null }> = {
  Vancouver: { entireSpace: null, privateRoom: null }, // No cap - principal residence required
  'North Vancouver': { entireSpace: 182, privateRoom: null }, // 182-day cap
  Burnaby: { entireSpace: 28, privateRoom: 90 }, // 28-day cap for entire space
  Surrey: { entireSpace: null, privateRoom: null }, // No cap
  Langley: { entireSpace: null, privateRoom: null }, // No cap
  Victoria: { entireSpace: null, privateRoom: null }, // No cap
  Whistler: { entireSpace: null, privateRoom: null }, // No cap
  Calgary: { entireSpace: null, privateRoom: null }, // No cap
  Edmonton: { entireSpace: null, privateRoom: null }, // No cap
  Toronto: { entireSpace: null, privateRoom: null }, // No cap
};

const PROPERTY_TYPES = [
  { id: 'entire-home', label: 'Entire Home', icon: 'ri-home-4-line' },
  { id: 'condo', label: 'Condo', icon: 'ri-building-2-line' },
  { id: 'townhouse', label: 'Townhouse', icon: 'ri-building-line' },
  { id: 'suite', label: 'Suite / Room', icon: 'ri-door-open-line' },
];

export function CalculatorPanel() {
  const [city, setCity] = useState('Vancouver');
  const [propertyType, setPropertyType] = useState('entire-home');
  const [bedrooms, setBedrooms] = useState(2);
  const [bathrooms, setBathrooms] = useState(1);
  const [sqft, setSqft] = useState(900);
  const [hasParking, setHasParking] = useState(true);
  const [hasWifi, setHasWifi] = useState(true);
  const [petFriendly, setPetFriendly] = useState(false);
  const [hasPool, setHasPool] = useState(false);
  const [hasAC, setHasAC] = useState(true);
  const [isNewlyRenovated, setIsNewlyRenovated] = useState(false);

  // City-specific night caps based on 2026 regulations
  const getNightCap = (city: string, propertyType: string): number | null => {
    // Private rooms have no cap except Burnaby
    if (propertyType === 'Private Room') {
      return city === 'Burnaby' ? 90 : null;
    }

    // Entire space caps - only North Vancouver and Burnaby have caps
    const caps: Record<string, number | null> = {
      Vancouver: null, // No cap - 365 days allowed if principal residence
      'North Vancouver': 182,
      Burnaby: 28,
      Surrey: null, // No cap
      Langley: null, // No cap
      Victoria: null, // No cap
      Toronto: null, // No cap
      Whistler: null, // No cap
      Calgary: null, // No cap
      Edmonton: null, // No cap
    };

    return caps[city] ?? null; // Default to no cap for unlisted cities
  };

  const calculations = useMemo(() => {
    const cityInfo = CITY_DATA[city] ?? CITY_DATA['Vancouver'];
    const nightCap = NIGHT_CAPS[city] ?? NIGHT_CAPS['Vancouver'];

    // Base nightly rate from city
    let nightlyRate = cityInfo.baseRate;

    // Bedroom multiplier
    if (bedrooms === 0) nightlyRate *= 0.55;
    else if (bedrooms === 1) nightlyRate *= 0.85;
    else if (bedrooms === 2) nightlyRate *= 1.0;
    else if (bedrooms === 3) nightlyRate *= 1.3;
    else if (bedrooms === 4) nightlyRate *= 1.55;
    else nightlyRate *= 1.55 + (bedrooms - 4) * 0.2;

    // Bathroom bonus
    if (bathrooms >= 2) nightlyRate *= 1.08;
    if (bathrooms >= 3) nightlyRate *= 1.05;

    // Sq ft adjustment
    if (sqft >= 1500) nightlyRate *= 1.15;
    else if (sqft >= 1200) nightlyRate *= 1.08;
    else if (sqft >= 900) nightlyRate *= 1.0;
    else if (sqft >= 600) nightlyRate *= 0.92;
    else nightlyRate *= 0.82;

    // Property type adjustment
    const isPrivateRoom = propertyType === 'suite';
    if (propertyType === 'entire-home') nightlyRate *= 1.1;
    else if (propertyType === 'condo') nightlyRate *= 1.0;
    else if (propertyType === 'townhouse') nightlyRate *= 1.05;
    else if (propertyType === 'suite') nightlyRate *= 0.6;

    // Amenity bonuses
    if (hasParking) nightlyRate *= 1.05;
    if (petFriendly) nightlyRate *= 1.04;
    if (hasPool) nightlyRate *= 1.08;
    if (isNewlyRenovated) nightlyRate *= 1.07;
    if (hasAC) nightlyRate *= 1.02;

    nightlyRate = Math.round(nightlyRate);

    // Occupancy
    let occupancy = cityInfo.occupancy;
    if (propertyType === 'suite') occupancy += 3;
    if (bedrooms <= 1) occupancy += 2;
    if (hasWifi) occupancy += 1;
    occupancy = Math.min(95, occupancy);

    // Calculate maximum nights allowed per year
    let maxNightsPerYear: number | null = null;
    if (isPrivateRoom) {
      maxNightsPerYear = nightCap.privateRoom;
    } else {
      maxNightsPerYear = nightCap.entireSpace;
    }

    // Calculate potential nights based on occupancy
    const potentialNightsPerYear = Math.round(365 * (occupancy / 100));
    
    // Apply night cap if exists
    const actualNightsPerYear = maxNightsPerYear !== null 
      ? Math.min(potentialNightsPerYear, maxNightsPerYear)
      : potentialNightsPerYear;

    const monthlyRevenue = Math.round(
      nightlyRate * 30 * (occupancy / 100)
    );
    
    // Annual revenue based on actual allowed nights
    const annualRevenue = Math.round(nightlyRate * actualNightsPerYear);

    return {
      nightlyRate,
      occupancyRate: occupancy,
      monthlyRevenue,
      annualRevenue,
      maxNightsPerYear,
      actualNightsPerYear,
      isCapped: maxNightsPerYear !== null && potentialNightsPerYear > maxNightsPerYear,
    };
  }, [
    city,
    propertyType,
    bedrooms,
    bathrooms,
    sqft,
    hasParking,
    hasWifi,
    petFriendly,
    hasPool,
    hasAC,
    isNewlyRenovated,
  ]);

  const toggleAmenity = useCallback(
    (setter: React.Dispatch<React.SetStateAction<boolean>>) => {
      setter((prev) => !prev);
    },
    []
  );

  return (
    <section className="py-10 lg:py-14">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* Left: Input Panel */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 lg:p-8">
              {/* City Selection */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-[#505A5A] mb-3">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#882629]/10">
                      <i className="ri-map-pin-2-line text-[#882629] text-base"></i>
                    </div>
                    <span>City / Location</span>
                  </div>
                </label>
                <div className="flex flex-wrap gap-2">
                  {Object.keys(CITY_DATA).map((c) => (
                    <button
                      key={c}
                      onClick={() => setCity(c)}
                      className={`px-4 py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                        city === c
                          ? 'bg-[#882629] text-white shadow-md'
                          : 'bg-gray-100 text-[#505A5A] hover:bg-gray-200'
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              {/* Property Type */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#882629]/10">
                    <i className="ri-home-line text-[#882629] text-base"></i>
                  </div>
                  <span className="text-sm font-semibold text-[#505A5A]">
                    Property Type
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {PROPERTY_TYPES.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setPropertyType(type.id)}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                        propertyType === type.id
                          ? 'border-[#882629] bg-[#F9F5F5] shadow-sm'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div
                        className={`w-10 h-10 flex items-center justify-center rounded-full ${
                          propertyType === type.id
                            ? 'bg-[#882629] text-white'
                            : 'bg-gray-100 text-[#505A5A]'
                        }`}
                      >
                        <i className={`${type.icon} text-lg`}></i>
                      </div>
                      <span
                        className={`text-xs font-medium whitespace-nowrap ${
                          propertyType === type.id
                            ? 'text-[#882629]'
                            : 'text-[#505A5A]'
                        }`}
                      >
                        {type.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Sliders */}
              <PropertySlider
                label="Bedrooms"
                value={bedrooms}
                min={0}
                max={6}
                step={1}
                icon="ri-hotel-bed-line"
                onChange={setBedrooms}
                formatValue={(v) => (v === 0 ? 'Studio' : `${v} BR`)}
              />

              <PropertySlider
                label="Bathrooms"
                value={bathrooms}
                min={1}
                max={5}
                step={1}
                icon="ri-drop-line"
                onChange={setBathrooms}
                formatValue={(v) => `${v} BA`}
              />

              <PropertySlider
                label="Square Footage"
                value={sqft}
                min={300}
                max={3000}
                step={50}
                suffix=" sq ft"
                icon="ri-ruler-line"
                onChange={setSqft}
              />

              {/* Amenities */}
              <div className="mt-8">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#882629]/10">
                    <i className="ri-sparkling-line text-[#882629] text-base"></i>
                  </div>
                  <span className="text-sm font-semibold text-[#505A5A]">
                    Amenities &amp; Features
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    {
                      label: 'Parking',
                      icon: 'ri-parking-box-line',
                      active: hasParking,
                      toggle: () => toggleAmenity(setHasParking),
                    },
                    {
                      label: 'WiFi',
                      icon: 'ri-wifi-line',
                      active: hasWifi,
                      toggle: () => toggleAmenity(setHasWifi),
                    },
                    {
                      label: 'Pet Friendly',
                      icon: 'ri-bear-smile-line',
                      active: petFriendly,
                      toggle: () => toggleAmenity(setPetFriendly),
                    },
                    {
                      label: 'Pool / Hot Tub',
                      icon: 'ri-water-flash-line',
                      active: hasPool,
                      toggle: () => toggleAmenity(setHasPool),
                    },
                    {
                      label: 'Air Conditioning',
                      icon: 'ri-temp-cold-line',
                      active: hasAC,
                      toggle: () => toggleAmenity(setHasAC),
                    },
                    {
                      label: 'Newly Renovated',
                      icon: 'ri-hammer-line',
                      active: isNewlyRenovated,
                      toggle: () => toggleAmenity(setIsNewlyRenovated),
                    },
                  ].map((amenity) => (
                    <button
                      key={amenity.label}
                      onClick={amenity.toggle}
                      className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                        amenity.active
                          ? 'border-[#882629] bg-[#F9F5F5]'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div
                        className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${
                          amenity.active
                            ? 'bg-[#882629] text-white'
                            : 'bg-gray-100 text-[#505A5A]'
                        }`}
                      >
                        <i className={`${amenity.icon} text-sm`}></i>
                      </div>
                      <span
                        className={`text-xs font-medium whitespace-nowrap ${
                          amenity.active
                            ? 'text-[#882629]'
                            : 'text-[#505A5A]'
                        }`}
                      >
                        {amenity.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Disclaimer */}
              <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-amber-600 text-sm"></i>
                  </div>
                  <p className="text-xs text-amber-800 leading-relaxed">
                    This is an estimate based on average market data for{' '}
                    {city}. Actual revenue depends on listing quality,
                    seasonality, guest reviews, and local regulations. For a
                    personalized assessment, book a free call with our team.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Revenue Display */}
          <div className="lg:col-span-5">
            {/* Mobile: Show revenue summary at top */}
            <div className="lg:hidden mb-6 bg-gradient-to-br from-[#882629] to-[#6E1F22] rounded-2xl p-5 text-center shadow-xl">
              <p className="text-white/80 text-xs font-medium tracking-wider uppercase mb-1">
                Estimated Monthly Revenue
              </p>
              <p
                className="text-white text-4xl font-bold"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                ${calculations.monthlyRevenue.toLocaleString()}
              </p>
              <p className="text-white/60 text-xs mt-1">
                ${calculations.nightlyRate}/night ·{' '}
                {calculations.occupancyRate}% occupancy
              </p>
            </div>

            <div className="hidden lg:block">
              <RevenueDisplay
                monthlyRevenue={calculations.monthlyRevenue}
                nightlyRate={calculations.nightlyRate}
                occupancyRate={calculations.occupancyRate}
                annualRevenue={calculations.annualRevenue}
                bedrooms={bedrooms}
                city={city}
                maxNightsPerYear={calculations.maxNightsPerYear}
                actualNightsPerYear={calculations.actualNightsPerYear}
                isCapped={calculations.isCapped}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
